import { SetStateAction, useContext, useEffect, useRef, useState } from "react";
import "./Listing.scss";
import {
  Bullets,
  Pagination,
  RightSidePanel,
  SkeletonList,
  TemplateSearch,
  showToast,
} from "ui-components";
import SubmitAssessmentListingIcon from "../../../../utils/Images/SubmitAssessmentListingIcon";
import { getApplicantAssessmentList } from "../../../../service/SubmitAssessment/SubmitAssessment";
import { useTranslation } from "react-i18next";
import DataNotAvailable from "../../../../utils/DataNotFound/DataNotAvailable";
import {
  convertDate,
  handleKeyPressSearch,
  manageDuplicateSkills,
  removeFromQueryParams,
  setInQueryParams,
} from "../../../../utils/Common/Constant";
import Footer from "../../../../components/Footer/Footer";
import ListingFilter from "./ListingFilter";
import useDebounce from "../../../../utils/Hooks/useDebounce";
import SubmitAssessmentHeader from "../../../../components/SubmitAssessment/Header/SubmitAssessmentHeader";
import Walkthrough from "../../../../components/WalkThrough/Walkthrough";
import FollowUpChat from "../../../../components/FollowUpChat/FollowUpChat";
import UserImage from "../../../../utils/Images/UserImage";
import FollowUpIcon from "../../../../utils/Images/FollowUpIcon";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WalkthroughContext } from "../../../../context/WalkthroughContext/WalkthroughContext";
const Listing = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [listing, setListing] = useState([]);
  const [listingFilter, setlistingFilter] = useState<string>("All");
  const [listingSearch, setListingSearch] = useState<string>("");
  const searchAssessment = useDebounce(listingSearch.trim(), 700);
  const listRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const applicantId = localStorage.getItem("applicantId") || "";
  const [pageSize, setPageSize] = useState(10);
  const [searchParams] = useSearchParams();
  const [openFollowupSidebar, setOpenFollowupSidebar] = useState<any>(
    searchParams.get("followup") ?? null,
  );
  const [assessmentListLoader, setAssessmentListLoader] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const {
    startWalkthrough,
    isHeaderWalkthroughDone,
    setIsHeaderWalkthroughDone,
  } = useContext(WalkthroughContext) || {};
  const navigate = useNavigate();
  // Get current language
  const currentLanguage = i18n.language;
  const buttonStatus: any = {
    New: t("GENERAL.START"),
    Initiated: t("GENERAL.RESUME"),
    Submitted: t("GENERAL.SUBMITTED"),
    Deferred: t("GENERAL.HIDE"),
  };

  const handleListingFilter = (value: string) => {
    setlistingFilter(value);
    setCurrentPage(1);
  };
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleAssessmentList();
        setlistingFilter("All");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleAssessmentList = async () => {
    setAssessmentListLoader(true);
    const res = await getApplicantAssessmentList(
      applicantId,
      listingFilter,
      currentPage,
      pageSize,
      searchAssessment,
    );
    setListing(res?.data ?? []);
    setTotalCount(res?.totalCount);
    setAssessmentListLoader(false);
  };

  useEffect(() => {
    handleAssessmentList();
  }, [listingFilter, currentPage, pageSize, searchAssessment]);

  useEffect(() => {
    // Scroll to the top of the list when the current page changes
    if (listRef.current) {
      listRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentPage]);

  function getResultStatus(applicationStatus: string, resultStatus: any) {
    switch (applicationStatus) {
      case "Terminated":
        return "Terminated";
      case "New":
      case "Initiated":
        return "-";
      case "Submitted":
        return resultStatus === "Hold"
          ? "On Hold"
          : (resultStatus ?? "Awaited");
      case "Barred":
        return "Barred";
      case "Deferred":
        return "Deferred";
    }
  }
  const searchRef: any = useRef(null);
  const startAssessRef: any = useRef(null);
  const assessDetailBox: any = useRef(null);
  const walkthroughSteps = startAssessRef.current
    ? [
        { content: t("GENERAL.SEARCH_ASSESSMENT"), ref: searchRef },
        {
          content: t("GENERAL.DETAILS_OF_ASSESSMENT"),
          ref: assessDetailBox,
        },
        {
          content: t("GENERAL.START_RESUME"),
          ref: startAssessRef,
        },
      ]
    : [
        { content: t("GENERAL.SEARCH_ASSESSMENT"), ref: searchRef },
        {
          content: t("GENERAL.DETAILS_OF_ASSESSMENT"),
          ref: assessDetailBox,
        },
      ];
  const handleWalkthroughComplete = () => {
    setIsHeaderWalkthroughDone(false);
  };
  return (
    <>
      <SubmitAssessmentHeader
        hideHeaderWalkthrough={!startWalkthrough}
        showGuide={true}
      />
      {isHeaderWalkthroughDone && (
        <Walkthrough
          steps={walkthroughSteps}
          onComplete={handleWalkthroughComplete}
          hasPreviousButton={true}
          showSkipNextTogether={true}
          offsetTop={-5}
          offsetLeft={-5}
          offsetWidth={+12}
          offsetHeight={+10}
        />
      )}
      <div
        ref={listRef}
        style={{
          overflowY: "auto",
          height: "100vh",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        className="container-fluid assessmentListingHead .hide-scrollbar"
      >
        <div className="listingTitle">
          <h2>{t("SUBMIT_ASSESSMENT_MY_ASSESSMENT")}</h2>
        </div>
        <div className="listingSearch" ref={searchRef}>
          <TemplateSearch
            onChange={(e: SetStateAction<string>) => {
              setListingSearch(e);
              setCurrentPage(1);
            }}
            placeholder={t("SUBMIT_ASSESSMENT.SEARCH_PLACEHOLDER")}
            value={listingSearch}
            handleKeyPress={handleKeyPressSearch}
            iconName="searchList"
          />
        </div>
        {/* filters start */}
        <div className="listingHead">
          <ListingFilter
            handleListingFilter={handleListingFilter}
            listingFilter={listingFilter}
          />
        </div>
        {/* filters end */}
        <div className="listingMain">
          {assessmentListLoader ? (
            Array.from({ length: 5 }).map((_, index) => (
              <SkeletonList
                key={`skeleton-${index}`}
                numberOfTitles={1}
                numberofTextLines={3}
                titleSize={18}
                textSize={12}
                thumbnailHeight={216}
                thumbnailWidth={200}
                itemsGap={20}
              />
            ))
          ) : listing?.length === 0 ? (
            <DataNotAvailable />
          ) : (
            (listing ?? []).map((item: any, index) => (
              <div
                className="assessmentListing mb-4"
                key={item?.assessmentId + index}
                ref={index === 0 ? assessDetailBox : null}
              >
                <div className="listing">
                  <div className="section-1">
                    <div className="logo">
                      <SubmitAssessmentListingIcon />
                      <p>{item?.applicationStatus}</p>
                    </div>
                    <div className="content">
                      <div className="d-flex gap-2">
                        <span className="title">{item?.title}</span>
                      </div>
                      <div className="skill">
                        <div className="d-flex gap-2 allskill">
                          {(
                            manageDuplicateSkills(item?.assessmentSkills) || []
                          ).map((item: any, index: number) => {
                            return (
                              <Bullets
                                key={index}
                                skillName={item?.skillName}
                              />
                            );
                          })}
                        </div>
                      </div>
                      <div className="assesmentDetails mt-2">
                        <div className="assessment-company">
                          <p className="">
                            <b>{t("SUBMIT_ASSESSMENT_COMPANY_PROVIDER")}</b>
                          </p>
                          <span className="p-content">
                            {item?.companyName ?? "N/A"}
                          </span>
                        </div>

                        <div className="question">
                          <p className="">
                            <b>{t("GENERAL.QUESTIONS")}</b>
                          </p>
                          <p className="p-content">{item?.totalQuestions}</p>
                        </div>
                        <div className="time">
                          <p className="">
                            <b>{t("GENERAL.TIME")}</b>
                          </p>
                          <p className="p-content">{`${item?.totalTime} ${t("GENERAL.MINS")}`}</p>
                        </div>
                        <div className="result">
                          <p className="">
                            <b>{t("GENERAL.RESULT")}</b>
                          </p>
                          <p className="p-content">
                            {getResultStatus(
                              item?.applicationStatus,
                              item?.resultStatus,
                            )}
                            {/* {item?.resultStatus ? item?.resultStatus : "---"} */}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="section-2">
                    {item?.applicationStatus !== "New" && (
                      <div
                        className="followupIcon"
                        onClick={() => {
                          setOpenFollowupSidebar(item.assessmentId);
                          setInQueryParams(
                            queryParams,
                            "followup",
                            item.assessmentId,
                            navigate,
                          );
                        }}
                        title={t("GENERAL.FOLLOW_UP")}
                      >
                        <FollowUpIcon />
                      </div>
                    )}
                    <p className="mt-3">
                      {convertDate(item?.createdAt, false, true)}
                    </p>
                    <div className="d-flex gap-2">
                      {(item?.applicationStatus === "New" ||
                        item?.applicationStatus === "Initiated") && (
                        <button
                          ref={index == 0 ? startAssessRef : null}
                          onClick={() => {
                            if (item?.applicationStatus == "Submitted") {
                              showToast(t("GENERAL.COMING_SOON"), "success");
                            } else {
                              window.open(
                                `/${currentLanguage}/assessmentdetails/${item?.assessmentId}?appId=${item?.applicantId}&compId=${item?.companyId}&loggedIn=true`,
                                "_blank",
                                "noopener",
                              );
                            }
                          }}
                          className="viewBtn"
                        >
                          {buttonStatus[item?.applicationStatus]} &nbsp;
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {openFollowupSidebar === item.assessmentId && (
                  <RightSidePanel
                    show={true}
                    handleClose={() => {
                      setOpenFollowupSidebar(false);
                      removeFromQueryParams(queryParams, "followup", navigate);
                    }}
                    title={item?.companyName}
                    className="followUpChatPanel"
                  >
                    <div
                      className={`userImageBorder ${(item?.applicationStatus === "Submitted" || item?.applicationStatus === "Barred") && "pointerCursor"}`}
                    >
                      <UserImage />
                    </div>
                    <FollowUpChat
                      assessmentTitle={item?.title}
                      applicantId={applicantId}
                      assessmentId={item?.assessmentId}
                      companyId={item?.companyId}
                      resultStatus={getResultStatus(
                        item?.applicationStatus,
                        item?.resultStatus,
                      )}
                      invitedOn={item?.invitedOn}
                      submittedOn={item?.submittedOn}
                    />
                  </RightSidePanel>
                )}
              </div>
            ))
          )}
        </div>
        <div className="row">
          <div className="col-md-12">
            <Pagination
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={totalCount || 0}
              pageSize={pageSize}
              onPageChange={(page: number) => setCurrentPage(page)}
              ofLabel={t("GENERAL.OF")}
              resultLabel={t("GENERAL.RESULTS")}
              setPageSize={setPageSize}
            />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Listing;
